import { useMemo, useState } from "react";
import {
  CircleStop,
  Cloud,
  Pencil,
  Play,
  RefreshCw,
  Upload,
} from "lucide-react";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { buildDocumentPath, navPaths } from "@/core/constants";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Pagination } from "@/components/ui/pagination";
import { KbBreadcrumb } from "./components/kb-breadcrumb";
import { DocumentRow, type DocumentRowBusy } from "./components/document-row";
import { EditKbDialog } from "./components/edit-kb-dialog";
import { UploadDocumentsDialog } from "./components/upload-documents-dialog";
import { SharePointSyncDialog } from "./components/sharepoint-sync-dialog";
import { DeleteDocumentDialog } from "./components/delete-document-dialog";
import { DocumentDetailDialog } from "./components/document-detail-dialog";
import { ParseAllDialog } from "./components/parse-all-dialog";
import { StopAllDialog } from "./components/stop-all-dialog";
import { useKnowledgeBase } from "./hooks/use-knowledge-base";
import { useDocuments } from "./hooks/use-documents";
import {
  useParseAllDocuments,
  useParseDocuments,
  useReparseAllDocuments,
  useReparseDocuments,
} from "./hooks/use-parse-documents";
import { useStopAllDocuments } from "./hooks/use-stop-all-documents";
import { useDeleteDocument } from "./hooks/use-delete-document";
import { useDownloadDocument } from "./hooks/use-download-document";
import { useCancelDocument } from "./hooks/use-cancel-document";
import { useRetryDocument } from "./hooks/use-retry-document";
import type { KnowledgeDocument } from "../domain/entities/document";

const PAGE_SIZE = 20;

export const KnowledgeBaseDetailPage = () => {
  const { id = "" } = useParams<{ id: string }>();
  if (!id) return <Navigate to="/knowledge-bases" replace />;

  return <Detail id={id} />;
};

const Detail = ({ id }: { id: string }) => {
  const { docId } = useParams<{ docId?: string }>();
  const navigate = useNavigate();
  const viewDocId = docId ?? null;

  const [searchParams, setSearchParams] = useSearchParams();
  const searchSuffix = searchParams.toString()
    ? `?${searchParams.toString()}`
    : "";
  const openDoc = (docIdToOpen: string) =>
    navigate(`${buildDocumentPath(id, docIdToOpen)}${searchSuffix}`);
  const closeDoc = () =>
    navigate(`${navPaths.knowledgeBases}/${id}${searchSuffix}`, {
      replace: true,
    });

  const pageParam = Number(searchParams.get("page"));
  const page = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;
  const setPage = (next: number) => {
    setSearchParams(
      (prev) => {
        const updated = new URLSearchParams(prev);
        if (next <= 1) updated.delete("page");
        else updated.set("page", String(next));
        return updated;
      },
      { replace: false },
    );
  };
  const [editOpen, setEditOpen] = useState(false);
  const [editKey, setEditKey] = useState(0);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [sharePointOpen, setSharePointOpen] = useState(false);
  const [parseAllOpen, setParseAllOpen] = useState(false);
  const [reparseAllOpen, setReparseAllOpen] = useState(false);
  const [stopAllOpen, setStopAllOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<KnowledgeDocument | null>(
    null,
  );
  
  const handleOpenEdit = () => {
    setEditKey((k) => k + 1);
    setEditOpen(true);
  };

  const { data: kb, isLoading: kbLoading } = useKnowledgeBase(id);
  const { data: docsData, isLoading: docsLoading } = useDocuments({
    kbId: id,
    page,
    limit: PAGE_SIZE,
  });

  const {
    mutate: parseOne,
    isPending: isParseOnePending,
    variables: parseOneVars,
  } = useParseDocuments();
  const {
    mutate: reparseOne,
    isPending: isReparseOnePending,
    variables: reparseOneVars,
  } = useReparseDocuments();
  const {
    mutate: cancelOne,
    isPending: isCancelOnePending,
    variables: cancelOneVars,
  } = useCancelDocument();
  const {
    mutate: retryOne,
    isPending: isRetryOnePending,
    variables: retryOneVars,
  } = useRetryDocument();
  const {
    mutate: downloadOne,
    isPending: isDownloadPending,
    variables: downloadVars,
  } = useDownloadDocument();
  const { mutate: parseAll, isPending: isParsingAll } =
    useParseAllDocuments(id);
  const { mutate: reparseAll, isPending: isReparsingAll } =
    useReparseAllDocuments(id);
  const { mutate: stopAll, isPending: stoppingAll } = useStopAllDocuments(id);
  const { mutate: deleteDoc, isPending: isDeleting } = useDeleteDocument();

  const documentRowBusy: DocumentRowBusy = useMemo(() => {
    if (isDownloadPending && downloadVars) {
      return { kind: "download", docId: downloadVars.docId };
    }
    if (isParseOnePending && parseOneVars?.[0] !== undefined) {
      return { kind: "parse", docId: parseOneVars[0] };
    }
    if (isReparseOnePending && reparseOneVars?.[0] !== undefined) {
      return { kind: "reparse", docId: reparseOneVars[0] };
    }
    if (isCancelOnePending && cancelOneVars !== undefined) {
      return { kind: "cancel", docId: cancelOneVars };
    }
    if (isRetryOnePending && retryOneVars !== undefined) {
      return { kind: "retry", docId: retryOneVars };
    }
    return null;
  }, [
    isDownloadPending,
    downloadVars,
    isParseOnePending,
    parseOneVars,
    isReparseOnePending,
    reparseOneVars,
    isCancelOnePending,
    cancelOneVars,
    isRetryOnePending,
    retryOneVars,
  ]);

  const items = docsData?.items ?? [];
  const total = docsData?.total ?? 0;

  /** Only know "every doc is still uploaded" when the loaded list is the full KB (one page). */
  const listCoversAllDocs = total > 0 && items.length === total;
  const reanalyzeAllDisabled =
    total === 0 ||
    (listCoversAllDocs && items.every((d) => d.status === "uploaded")) ||
    items.some((d) => d.status === "processing");

  if (kbLoading) {
    return (
      <div className="flex items-center justify-center p-16 text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (!kb) {
    return <Navigate to="/knowledge-bases" replace />;
  }

  const handleParseOne = (docId: string) => {
    parseOne([docId], {
      onSuccess: () => toast.success("Analyzing started"),
    });
  };

  const handleReparseOne = (docId: string) => {
    reparseOne([docId], {
      onSuccess: () => toast.success("Re-analyze started"),
    });
  };

  const handleCancel = (docId: string) => {
    cancelOne(docId, {
      onSuccess: () => toast.success("Analyzing cancelled"),
    });
  };

  const handleRetry = (docId: string) => {
    retryOne(docId, {
      onSuccess: () => toast.success("Retry started"),
    });
  };

  const handleDownload = (doc: KnowledgeDocument) => {
    downloadOne(
      { docId: doc.id, filename: doc.name },
      {
        onSuccess: () => toast.success("File saved"),
      },
    );
  };

  const handleParseAll = () => {
    parseAll(undefined, {
      onSuccess: () => {
        toast.success("Analyzing started for all documents");
        setParseAllOpen(false);
      },
      onError: () => {
        setParseAllOpen(false);
      },
    });
  };

  const handleReparseAll = () => {
    reparseAll(undefined, {
      onSuccess: () => {
        toast.success("Re-analyze started for all documents");
        setReparseAllOpen(false);
      },
      onError: () => {
        setReparseAllOpen(false);
      },
    });
  };

  const handleStopAll = () => {
    stopAll(undefined, {
      onSuccess: (count) => {
        toast.success(
          count > 0
            ? `Stopped ${count} document(s)`
            : "No documents were being analyzed",
        );
        setStopAllOpen(false);
      },
      onError: () => {
        setStopAllOpen(false);
      },
    });
  };

  const handleDelete = (docId: string) => {
    deleteDoc(docId, {
      onSuccess: () => {
        toast.success("Document deleted");
        setDeleteTarget(null);
      },
    });
  };

  return (
    <div className="flex flex-col gap-6 p-8">
      <KbBreadcrumb kbName={kb.name} />

      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-2xl font-semibold tracking-tight">
              {kb.name}
            </h1>
          </div>

          <div className="flex shrink-0 items-center gap-2 mt-0">
          <Button
            variant="ghost"
            size="icon"
            className="cursor-pointer"
            onClick={handleOpenEdit}
            aria-label="Edit knowledge base"
          >
            <Pencil className="size-4" />
          </Button>
          <Button
            variant="outline"
            className="cursor-pointer"
            onClick={() => setParseAllOpen(true)}
            disabled={total === 0}
          >
            <Play className="size-4" />
            Analyze all
          </Button>
          <Button
            variant="outline"
            className="cursor-pointer"
            onClick={() => setReparseAllOpen(true)}
            disabled={reanalyzeAllDisabled}
          >
            <RefreshCw className="size-4" />
            Re-analyze all
          </Button>
          <Button
            variant="outline"
            className="cursor-pointer"
            onClick={() => setStopAllOpen(true)}
            disabled={total === 0}
          >
            <CircleStop className="size-4" />
            Stop all
          </Button>
          <Button
            className="cursor-pointer"
            onClick={() => setUploadOpen(true)}
          >
            <Upload className="size-4" />
            Upload
          </Button>
          <Button
            variant="outline"
            className="cursor-pointer"
            onClick={() => setSharePointOpen(true)}
          >
            <Cloud className="size-4" />
            Sync from SharePoint
          </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-col divide-y rounded-lg border mt-2">
        {docsLoading ? (
          <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
            No documents uploaded yet.
          </div>
        ) : (
          items.map((doc) => (
            <DocumentRow
              key={doc.id}
              document={doc}
              onView={(d) => openDoc(d.id)}
              onDownload={handleDownload}
              onParse={handleParseOne}
              onReparse={handleReparseOne}
              onCancel={handleCancel}
              onRetry={handleRetry}
              onDelete={setDeleteTarget}
              busy={documentRowBusy}
            />
          ))
        )}
      </div>

      {total > PAGE_SIZE && (
        <div className="flex justify-end">
          <Pagination
            page={page}
            total={total}
            limit={PAGE_SIZE}
            onPageChange={setPage}
          />
        </div>
      )}

      <EditKbDialog
        key={editKey}
        open={editOpen}
        kb={kb}
        onClose={() => setEditOpen(false)}
      />

      <UploadDocumentsDialog
        open={uploadOpen}
        kbId={id}
        onClose={() => setUploadOpen(false)}
      />

      <SharePointSyncDialog
        open={sharePointOpen}
        kbId={id}
        onClose={() => setSharePointOpen(false)}
      />

      <ParseAllDialog
        open={parseAllOpen}
        kbName={kb.name}
        onClose={() => setParseAllOpen(false)}
        onConfirm={handleParseAll}
        isPending={isParsingAll}
      />

      <ParseAllDialog
        open={reparseAllOpen}
        mode="reparse"
        kbName={kb.name}
        onClose={() => setReparseAllOpen(false)}
        onConfirm={handleReparseAll}
        isPending={isReparsingAll}
      />

      <StopAllDialog
        open={stopAllOpen}
        kbName={kb.name}
        onClose={() => setStopAllOpen(false)}
        onConfirm={handleStopAll}
        isPending={stoppingAll}
      />

      <DeleteDocumentDialog
        target={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        isPending={isDeleting}
      />

      <DocumentDetailDialog docId={viewDocId} onClose={closeDoc} />
    </div>
  );
};
